import { useTranslation } from 'react-i18next';
import styles from './Liste.module.scss';
import { Cell, ColumnInterface } from 'react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter, SGSCard, SGSColumnFilter, SGSTable, SGSTooltip } from 'components';
import { Box, Typography } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { affilieService } from 'core/services';
import moment from 'moment';
import { useQueryConf } from 'core/utils/useQuery';
import { useQuery } from 'react-query';
import { StatusIncapiciteIcon } from 'features/dossierIncapacite/StatusIncapiciteIcon';
import { Affiliated } from 'core/models';
import { useAppContext } from 'core/contexts';

type Indemnisation = {
    id: number;
    lbNatureSinistre: string;
    nbreJourArret: string;
    dateDernierPayment: string;
    lbSouscrip: string;
    dtSinitre: string;
    dtCloture: string;
};

interface RouteParams {
    codAffilie: string;
}

type Props = {};
const ListDossierPrev: React.FC<Props> = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { setHeaderTitle } = useAppContext();
    const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
    const [filteredDossier, setFilteredDossier] = useState([]);
    const [souscrips, setSouscrips] = useState([]);

    let { codAffilie } = useParams<RouteParams>();

    const { isLoading, data: compensations } = useQuery([codAffilie, 'compensations'], async () => await affilieService.getCompensationsByAffiliateId(codAffilie), useQueryConf);

    const methods = useForm({
        defaultValues: {},
    });

    const handleFilters = useCallback(() => {
        const activeFiltersKeys = activeFilters.reduce<string[]>((r, f) => [...r, ...Object.keys(f)], []);
        if (activeFiltersKeys.length) {
            let list = compensations.dossiers.filter((e) => {
                const result = [];
                activeFiltersKeys.forEach((key) => {
                    const obj = activeFilters.find((x) => x[key]);
                    if (key !== 'lbSouscrip') {
                        const resp: any = obj[key];
                        if (obj) result.push(e[key].toString().includes(resp.toUpperCase()));
                    } else {
                        const souscrip = souscrips.find((e) => e === obj.lbSouscrip);
                        if (souscrip) result.push(e.lbSouscrip === souscrip);
                    }
                });
                return result.includes(false) ? false : true;
            });

            setFilteredDossier(list);
        } else {
            setFilteredDossier(compensations?.dossiers);
        }
    }, [activeFilters, compensations]);

    useEffect(() => {
        setHeaderTitle(compensations?.affilie);
        if (compensations?.dossiers) {
            const lbSouscripSet = new Set();
            compensations.dossiers.forEach((item) => lbSouscripSet.add(item.lbSouscrip));
            const lbSouscripList = Array.from(lbSouscripSet);
            setSouscrips(lbSouscripList);
        }
        handleFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compensations]);

    const columns = useMemo<ColumnInterface<Indemnisation>[]>(
        () => [
            {
                id: '0',
                Header: '',
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} justify-content-center text-center`,
                accessor: (row: Affiliated) => `${row.status} ${row.lbStatut}`,
                minWidth: 40,
                maxWidth: 40,
                disableFilters: true,
                Cell: ({ row: { original: row } }: Cell<Affiliated>) => {
                    const step = row.blAlert === '1' ? '3' : row.status;
                    return (
                        <SGSTooltip className="text-center" title={<Box>{row.lbStatut}</Box>}>
                            <Box>{StatusIncapiciteIcon(step)}</Box>
                        </SGSTooltip>
                    );
                },
                options: {
                    display: false,
                },
            },
            {
                Header: t('compensation.dossiers.disaster'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} `,
                accessor: 'lbNatureSinistre',
                maxWidth: 80,
                disableFilters: true,
                Cell: ({ row: { original: row } }: Cell<Indemnisation>) => {
                    return (
                        <Box component={'span'}>
                            {row.lbNatureSinistre} du {moment(row.dtSinitre, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                        </Box>
                    );
                },
            },
            {
                Header: t('compensation.dossiers.souscrip'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell}`,
                accessor: 'lbSouscrip',
                maxWidth: 120,
                disableFilters: true,
                Cell: ({ row: { original: row } }: Cell<Indemnisation>) => {
                    return (
                        <Box component={'span'} className={styles.strong}>
                            {row.lbSouscrip}
                        </Box>
                    );
                },
            },
            {
                Header: t('compensation.dossiers.InterruptionDuration'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} justify-content-center text-center`,
                accessor: 'nbreJourArret',
                maxWidth: 100,
                disableFilters: true,
                Cell: ({ row: { original: row } }: Cell<Indemnisation>) => {
                    return <Box component={'span'}>{row.nbreJourArret ? row.nbreJourArret : '-'}</Box>;
                },
            },
            {
                Header: t('compensation.dossiers.LastPaymentDate'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} justify-content-center text-center`,
                accessor: 'dateDernierPayment',
                maxWidth: 100,
                disableFilters: true,
                Cell: ({ row: { original: row } }: Cell<Indemnisation>) => {
                    return <Box component={'span'}>{moment(row.dateDernierPayment).isValid() ? moment(row.dateDernierPayment).format('DD/MM/YYYY') : '-'}</Box>;
                },
            },
            {
                Header: t('compensation.dossiers.reference'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell}`,
                accessor: 'id',
                maxWidth: 80,
                disableFilters: true,
            },
            {
                Header: t('compensation.dossiers.dtCloture'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} justify-content-center text-center`,
                accessor: 'dtCloture',
                maxWidth: 60,
                disableFilters: true,
                Cell: ({ row: { original: row } }: Cell<Indemnisation>) => {
                    return <Box component={'span'}>{moment(row.dtCloture).isValid() ? moment(row.dtCloture).format('DD/MM/YYYY') : '-'}</Box>;
                },
            },
            {
                Header: '',
                accessor: 'none' as any,
                className: 'p-0',
                disableFilters: true,
                disableSortBy: true,
                minWidth: 25,
                maxWidth: 25,
                Cell: ({ row }: Cell<Indemnisation>) => {
                    return (
                        <Box className={styles.actions}>
                            <SGSTooltip title={<Box>{t('details')}</Box>}>
                                <div className={styles.actions__menu} onClick={() => handleDetails(row)}>
                                    <FontAwesomeIcon icon={faEye} size="lg" />
                                </div>
                            </SGSTooltip>
                        </Box>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t],
    );

    const filterableColumnLabels = {
        id: { value: t('compensation.search.reference'), type: 'input' },
        dtSinitre: { value: t('compensation.search.dateSinitre'), type: 'date' },
        lbSouscrip: {
            value: t('compensation.search.souscrip'),
            type: 'autocomplete',
            options: souscrips.map((r) => ({
                value: r,
                label: r,
            })),
        },
    };

    useEffect(() => {
        handleFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    const handleChangeFilter = (filters) => {
        setActiveFilters([...filters]);
    };

    const handleDetails = (row) => {
        const reference = row.original?.id;
        history.push('/affiliations/indemnisation/dossiers/' + reference);
    };

    return (
        <>
            <Box>
                <Box display="flex" className="my-2" justifyContent="left" alignItems="center">
                    <FormProvider {...methods}>
                        <SGSColumnFilter columns={filterableColumnLabels} className="mr-5" filters={activeFilters} onFiltersChange={handleChangeFilter} customLabel={t('filters.on*')} />
                    </FormProvider>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Box sx={{ flex: '1 1 auto' }}>
                        <Typography className={styles.filtersLegend}>
                            Accédez rapidement aux différents dossiers d'indemnisation. Cliquez sur la référence du dossier pour en savoir plus sur son avancement
                        </Typography>
                    </Box>
                </Box>

                <SGSCard color="default" className="w-100" contentStyle={styles.cardContent}>
                    <SGSTable onRowClick={handleDetails} columns={columns} data={filteredDossier || []} loading={isLoading}></SGSTable>
                </SGSCard>
            </Box>
        </>
    );
};

export default ListDossierPrev;
